/**
 * This directive will execute a function of your choice when you click outside the element
 * Add the directive on any element you want i.e:
 *
 * <AtomButton v-closable="{ executable: close, exclude: '.ignore-close' }" />
 *
 * In the button component itself, make sure you have a function called "close" as in this example.
 *
 * The function "close" will be called upon when the requirements are met. Meaning you can use any
 * function from the component you use the directive on without any restrictions.
 *
 * You can add a selector, if the target element.closest(selector) returns an element, the executable is not called.
 */

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('closable', {
    mounted(el, binding) {
      const executable = binding.value.executable
      const excludeSelector = binding.value.exclude

      // Stored on the element (as in the viewport directive) so every v-closable element keeps its own
      // handler: the directive is used inside v-for, where a shared handler would leak on unmount.
      el._closableDirective_onOutsideClick = (e: MouseEvent) => {
        const target = e.target instanceof Element ? e.target : null

        // Check that the click happened outside the v-closable element
        if (el.contains(target)) {
          return
        }

        // Check that where we clicked does not have a parent that is excluded
        if (excludeSelector && target?.closest(excludeSelector)) {
          return
        }

        // execute code here that should fire, code should be in the component itself
        if (typeof executable === 'function') {
          executable()
        } else if (typeof executable === 'string' && (binding.instance as Record<string, any> | null)?.[executable]) {
          ;(binding.instance as Record<string, any>)[executable]()
        }
      }

      // Add the event handler the will be temporarily available
      document.addEventListener('click', el._closableDirective_onOutsideClick)
      document.addEventListener('touchstart', el._closableDirective_onOutsideClick)
    },
    unmounted(el) {
      // when the element is removed, unbind it to safe performance
      document.removeEventListener('click', el._closableDirective_onOutsideClick)
      document.removeEventListener('touchstart', el._closableDirective_onOutsideClick)
    },
  })
})
